import { csvDateFormatIncludesTime, parseCsvDateWithFormats } from "./CsvDateFormats";
import { processTemplate } from "@imminently/decisively-core";
import { mapRequestData, parseCSV } from "./RequestMapping";
import { normalizeMappedRecords } from "./ContactMapping";
import { JS_BEGINNING_OF_TIME_ISO, JS_END_OF_TIME_ISO } from "./Constants";

const ORGMODEL_REQUIRED_FIELDS_INTERNAL = ["subject", "subjectId", "relatedId"] as const;
const ORGMODEL_OPTIONAL_FIELDS_INTERNAL = ["validFrom", "validTo", "type"] as const;

export const ORGMODEL_REQUIRED_FIELDS = Object.freeze([...ORGMODEL_REQUIRED_FIELDS_INTERNAL]);
export const ORGMODEL_OPTIONAL_FIELDS = Object.freeze([...ORGMODEL_OPTIONAL_FIELDS_INTERNAL]);

type OrgModelRecord = {
  id?: string;
  subject: string;
  subjectId: string;
  relatedId: string;
  validFrom?: string;
  validTo?: string;
  type?: string;
};

type OrgModelInterval = {
  start: number;
  end: number;
  rowNumber: number;
};

type OrgModelMappingOptions = {
  validateCrossRows?: boolean;
};

const normalizeOrgModelFieldKey = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  let trimmed = raw;
  if (
    ((trimmed.startsWith("\"") && trimmed.endsWith("\"")) || (trimmed.startsWith("'") && trimmed.endsWith("'")))
    && trimmed.length >= 2
  ) {
    trimmed = trimmed.slice(1, -1).trim();
  }
  trimmed = trimmed.replace(/^[\"']+|[\"']+$/g, "").trim();
  if (!trimmed) return "";

  const compact = trimmed.replace(/\s+/g, "").toLowerCase();
  if (compact === "id") return "id";
  if (compact === "subject" || compact === "subjecttype") return "subject";
  if (compact === "subjectid") return "subjectId";
  if (compact === "relatedid") return "relatedId";
  if (compact === "validfrom") return "validFrom";
  if (compact === "validto") return "validTo";
  if (compact === "type") return "type";
  return trimmed;
};

const normalizeOrgModelRecordKeys = (record: Record<string, any>): Record<string, any> => {
  const normalized: Record<string, any> = {};
  for (const [key, value] of Object.entries(record || {})) {
    const normalizedKey = normalizeOrgModelFieldKey(key);
    if (!normalizedKey || normalized[normalizedKey] !== undefined) continue;
    normalized[normalizedKey] = value;
  }
  return normalized;
};

const isLikelyBase64 = (value: string): boolean => {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed.length % 4 !== 0) return false;
  return /^[A-Za-z0-9+/]+={0,2}$/.test(trimmed);
};

const encodeBase64 = (value: string): string => {
  if (typeof (globalThis as any).window === "undefined" && typeof Buffer !== "undefined") {
    return Buffer.from(value, "utf8").toString("base64");
  }
  return btoa(value);
};

const normalizeHeaderKey = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

const resolveRowHeaderKey = (header: string, row: Record<string, any>) => {
  if (Object.prototype.hasOwnProperty.call(row, header)) {
    return header;
  }

  const normalizedHeader = normalizeHeaderKey(header);
  if (!normalizedHeader) return undefined;

  return Object.keys(row).find((key) => normalizeHeaderKey(key) === normalizedHeader);
};

const parseQuotedLiteral = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (trimmed.length < 2) return undefined;
  const quote = trimmed[0];
  if ((quote !== "\"" && quote !== "'") || trimmed[trimmed.length - 1] !== quote) {
    return undefined;
  }
  const inner = trimmed.slice(1, -1);
  if (quote === "\"") {
    return inner.replace(/\\"/g, "\"");
  }
  return inner.replace(/\\'/g, "'");
};

const getColumnValue = (column: any, row: Record<string, any>) => {
  if (
    column?.value !== undefined &&
    !(typeof column.value === "string" && column.value.length === 0)
  ) {
    return column.value;
  }

  const header = typeof column?.header === "string" ? column.header : "";
  if (!header) return undefined;

  const quotedLiteral = parseQuotedLiteral(header);
  if (quotedLiteral !== undefined) {
    return quotedLiteral;
  }

  if (header.includes("{{")) {
    return processTemplate(header, row);
  }

  const resolvedHeaderKey = resolveRowHeaderKey(header, row);
  if (resolvedHeaderKey) {
    return row[resolvedHeaderKey];
  }

  return undefined;
};

const toCsvRows = async (input: unknown): Promise<Record<string, any>[]> => {
  if (Array.isArray(input)) {
    return input.filter((item) => item && typeof item === "object") as Record<string, any>[];
  }

  if (input && typeof input === "object") {
    const dataRows = (input as Record<string, unknown>).data;
    if (Array.isArray(dataRows)) {
      return dataRows.filter((item) => item && typeof item === "object") as Record<string, any>[];
    }
  }

  if (typeof input === "string") {
    const trimmed = input.trim();
    if (isLikelyBase64(trimmed)) {
      try {
        return await parseCSV(trimmed);
      } catch {
        // fall through to treat the value as raw CSV text.
      }
    }
    return parseCSV(encodeBase64(input));
  }

  throw new Error("CSV org model mapping requires CSV text, base64 CSV, or parsed rows");
};

const toNonEmptyString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : undefined;
};

const normalizeCsvDateBoundaryValue = (value: string | undefined, mappingDef: any): string | undefined => {
  if (!value) return value;

  const beginningToken = toNonEmptyString(mappingDef?.csvBeginningOfTimeValue);
  if (beginningToken && value === beginningToken) {
    return JS_BEGINNING_OF_TIME_ISO;
  }

  const endToken = toNonEmptyString(mappingDef?.csvEndOfTimeValue);
  if (endToken && value === endToken) {
    return JS_END_OF_TIME_ISO;
  }

  return value;
};

const isJsonDateString = (value: string): boolean => {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return !Number.isNaN(Date.parse(trimmed));
  }

  if (!/^([+-]\d{6}|\d{4})-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(trimmed)) {
    return false;
  }

  return !Number.isNaN(Date.parse(trimmed));
};

/**
 * An org model date as a full ISO datetime, which the org models service requires: a date on its own is that day's
 * start, midnight UTC (how the service already reads a date-only value).
 */
const normalizeJsonDateValue = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!isJsonDateString(trimmed)) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return `${trimmed}T00:00:00.000Z`;

  return new Date(trimmed).toJSON();
};

const normalizeOrgModelDateColumnValue = (
  value: unknown,
  column: any,
  mappingDef: any,
  rowNumber: number,
): unknown => {
  if (value === undefined || value === null) return value;
  const source = String(value).trim();
  if (!source) return value;

  const boundaryValue = normalizeCsvDateBoundaryValue(source, mappingDef);
  if (boundaryValue === JS_BEGINNING_OF_TIME_ISO || boundaryValue === JS_END_OF_TIME_ISO) return boundaryValue;

  const sourceFormat = toNonEmptyString(column?.dateFormat);
  if (!sourceFormat) return normalizeJsonDateValue(source);
  const { date: parsed, matchedFormat } = parseCsvDateWithFormats(source, sourceFormat);

  if (!parsed.isValid()) {
    const columnLabel = toNonEmptyString(column?.header) || toNonEmptyString(column?.field) || "unknown";
    throw new Error(`Unable to parse org model date '${source}' for column '${columnLabel}' at row ${rowNumber} with format '${sourceFormat}'`);
  }

  if (!csvDateFormatIncludesTime(matchedFormat)) return parsed.format("YYYY-MM-DD");

  return parsed.format("YYYY-MM-DD[T]HH:mm:ss.000[Z]");
};

const normalizeOrgModelRecord = (record: Record<string, any>, rowNumber: number, mappingDef?: any): OrgModelRecord => {
  const normalizedRecord = normalizeOrgModelRecordKeys(record);
  const id = toNonEmptyString(normalizedRecord.id);
  const subject = toNonEmptyString(normalizedRecord.subject);
  const subjectId = toNonEmptyString(normalizedRecord.subjectId);
  const relatedId = toNonEmptyString(normalizedRecord.relatedId);
  const validFromBoundary = normalizeCsvDateBoundaryValue(toNonEmptyString(normalizedRecord.validFrom), mappingDef);
  const validToBoundary = normalizeCsvDateBoundaryValue(toNonEmptyString(normalizedRecord.validTo), mappingDef);
  const validFrom = validFromBoundary === JS_BEGINNING_OF_TIME_ISO || validFromBoundary === JS_END_OF_TIME_ISO
    ? validFromBoundary
    : normalizeJsonDateValue(validFromBoundary);
  const validTo = validToBoundary === JS_BEGINNING_OF_TIME_ISO || validToBoundary === JS_END_OF_TIME_ISO
    ? validToBoundary
    : normalizeJsonDateValue(validToBoundary);
  const type = toNonEmptyString(normalizedRecord.type);

  const missing = ORGMODEL_REQUIRED_FIELDS_INTERNAL.filter((field) => {
    if (field === "subject") return !subject;
    if (field === "relatedId") return !relatedId;
    return !toNonEmptyString((normalizedRecord as any)[field]);
  });

  if (missing.length > 0) {
    throw new Error(`Org model mapping missing required fields at row ${rowNumber}: ${missing.join(", ")}`);
  }

  if (subjectId && relatedId && subjectId === relatedId) {
    throw new Error(`Org model mapping has invalid self-reference at row ${rowNumber}: subjectId cannot equal relatedId`);
  }

  if (validFrom && validTo) {
    if (Date.parse(validFrom) > Date.parse(validTo)) {
      throw new Error(`Org model mapping has invalid date range at row ${rowNumber}: validFrom cannot be after validTo`);
    }
  }

  return {
    ...(id ? { id } : {}),
    subject: subject!,
    subjectId: subjectId!,
    relatedId: relatedId!,
    ...(validFrom ? { validFrom } : {}),
    ...(validTo ? { validTo } : {}),
    ...(type ? { type } : {}),
  };
};

const toIntervalBounds = (record: OrgModelRecord, rowNumber: number): OrgModelInterval => {
  const start = record.validFrom ? Date.parse(record.validFrom) : Number.NEGATIVE_INFINITY;
  const end = record.validTo ? Date.parse(record.validTo) : Number.POSITIVE_INFINITY;

  if (Number.isNaN(start) || Number.isNaN(end)) {
    throw new Error(`Org model mapping has invalid date range at row ${rowNumber}: validFrom/validTo must be valid dates`);
  }
  if (start > end) {
    throw new Error(`Org model mapping has invalid date range at row ${rowNumber}: validFrom cannot be after validTo`);
  }

  return { start, end, rowNumber };
};

const intervalsOverlap = (a: OrgModelInterval, b: OrgModelInterval): boolean => (
  a.start <= b.end && b.start <= a.end
);

const mapOrgModelCsvRows = async (
  mappingDef: any,
  input: unknown,
  options: OrgModelMappingOptions = {},
): Promise<OrgModelRecord[]> => {
  const rows = await toCsvRows(input);
  const columns = Array.isArray(mappingDef?.columns) ? mappingDef.columns : [];
  const mappedRows: OrgModelRecord[] = [];
  const seenIntervalsByComposite = new Map<string, OrgModelInterval[]>();
  const validateCrossRows = options.validateCrossRows !== false;

  rows.forEach((row, rowIndex) => {
    const rowNumber = rowIndex + 1;
    const mapped: Record<string, any> = {};

    columns.forEach((column: any) => {
      const field = normalizeOrgModelFieldKey(column?.field);
      if (!field) return;

      const value = getColumnValue(column, row);
      if (value === undefined) return;

      if ((column?.identifier === true || column?.identifier === "true") && !mapped.id) {
        const identifierValue = toNonEmptyString(value);
        if (identifierValue) {
          mapped.id = identifierValue;
        }
      }

      mapped[field] = field === "validFrom" || field === "validTo"
        ? normalizeOrgModelDateColumnValue(value, column, mappingDef, rowNumber)
        : value;
    });

    if (Object.keys(mapped).length === 0) return;
    const normalized = normalizeOrgModelRecord(mapped, rowNumber, mappingDef);
    if (validateCrossRows) {
      const interval = toIntervalBounds(normalized, rowNumber);
      const compositeKey = `${String(normalized.subject || "").toLowerCase()}|${normalized.subjectId}|${normalized.relatedId}`;
      const seenIntervals = seenIntervalsByComposite.get(compositeKey) || [];
      const overlapping = seenIntervals.find((existing) => intervalsOverlap(existing, interval));
      if (overlapping) {
        throw new Error(
          `Org model mapping has overlapping date ranges at row ${rowNumber} for subject '${normalized.subject}', subjectId '${normalized.subjectId}', relatedId '${normalized.relatedId}'`,
        );
      }

      seenIntervals.push(interval);
      seenIntervalsByComposite.set(compositeKey, seenIntervals);
    }
    mappedRows.push(normalized);
  });

  return mappedRows;
};

export const mapOrgModelData = async (
  mappingDef: any,
  release: any,
  input: unknown,
  options: OrgModelMappingOptions = {},
): Promise<OrgModelRecord[]> => {
  if (!mappingDef) return [];
  const mode = typeof mappingDef.mode === "string" ? mappingDef.mode.toLowerCase() : "";

  if (mappingDef.type === "orgmodels" && mode === "csv") {
    return mapOrgModelCsvRows(mappingDef, input, options);
  }

  const mappedData = await mapRequestData(mappingDef, release, input);
  return normalizeMappedRecords(mappedData).map((record, index) => normalizeOrgModelRecord(record, index + 1));
};
